/**
 * Logo Connections Effect
 * Displays animated connecting lines between logos in adjacent carousel rows.
 * Lines follow logos as carousels move continuously.
 * Features auto-simulation mode that randomly connects logos.
 * Real hover overrides auto mode.
 *
 * Performance notes:
 * - Pauses entirely when container is offscreen (IntersectionObserver).
 * - Avoids per-logo getBoundingClientRect inside the RAF loop: logo offsets are
 *   cached once relative to the animated .marquee__wrapper, and each frame only
 *   reads one rect per marquee (~3 reads) instead of ~60+.
 * - Reads are batched before writes to prevent layout thrash.
 * - SVG line elements and class toggles are diffed instead of re-created.
 */

/**
 * @typedef {Object} CachedLogo
 * @property {HTMLElement} el
 * @property {number} dx
 * @property {number} dy
 * @property {number} w
 * @property {number} h
 */

/**
 * @typedef {Object} MarqueeCacheEntry
 * @property {HTMLElement} marquee
 * @property {HTMLElement | null} wrapper
 * @property {DOMRect} marqueeRect
 * @property {DOMRect} wrapperRect
 * @property {CachedLogo[]} logos
 */

/**
 * @typedef {Object} Connection
 * @property {HTMLElement} el
 * @property {number} x
 * @property {number} y
 * @property {number} distance
 */

class LogoConnectionsManager {
  constructor() {
    /** @type {HTMLElement | null} */
    this.activeElement = null;
    /** @type {SVGSVGElement | null} */
    this.svgOverlay = null;
    /** @type {HTMLElement[]} */
    this.marqueeComponents = [];
    /** @type {HTMLElement | null} */
    this.container = null;
    this.connectionColor = '#ffffff';

    /** @type {number | null} */
    this.animationFrameId = null;
    /** @type {Connection[]} */
    this.connections = [];

    this.targetFPS = 15;
    this.frameInterval = 1000 / this.targetFPS;
    this.lastFrameTime = 0;

    /** @type {'idle' | 'auto' | 'user'} */
    this.mode = 'idle';
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.autoModeTimeout = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.autoConnectionDurationTimeout = null;

    this.autoHoverDuration = 3000;

    /** @type {MarqueeCacheEntry[]} */
    this.marqueeCache = [];
    /** @type {DOMRect | null} */
    this.containerRect = null;

    this.ready = false;
    this.isInViewport = true; // optimistic until IntersectionObserver reports otherwise

    /** @type {SVGLineElement[]} */
    this.linePool = [];

    /** @type {Set<HTMLElement>} */
    this.prevConnectedSet = new Set();
    /** @type {HTMLElement | null} */
    this.prevActiveEl = null;

    // Scroll invalidation — marquee/container rects are re-read on next frame
    this.scrollDirty = false;

    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.resizeDebounceId = null;
    this.scrollHandler = null;
  }

  async setupEventListeners() {
    this.resolveConnectionColor();

    const middleMarqueeIndex = Math.floor(this.marqueeComponents.length / 2);
    const middleMarquee = this.marqueeComponents[middleMarqueeIndex];
    const durationInSeconds = parseFloat(middleMarquee?.getAttribute('data-auto-hover-duration')) || 3;
    this.autoHoverDuration = durationInSeconds * 1000;

    await this.waitForMarqueesReady();

    this.updateLayoutCache();
    this.createSVGOverlay();
    this.ready = true;

    this.attachPointerListeners();
    this.setupObservers();
  }

  waitForMarqueesReady() {
    return Promise.all(
      this.marqueeComponents.map(
        (m) =>
          /** @type {Promise<void>} */ (
            new Promise((resolve) => {
              if (m.hasAttribute('data-ready')) return resolve();
              const mo = new MutationObserver(() => {
                if (m.hasAttribute('data-ready')) {
                  mo.disconnect();
                  clearTimeout(safetyTimer);
                  resolve();
                }
              });
              mo.observe(m, { attributes: true, attributeFilter: ['data-ready'] });
              const safetyTimer = setTimeout(() => {
                mo.disconnect();
                resolve();
              }, 3000);
            })
          )
      )
    );
  }

  setupObservers() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.isInViewport = entry.isIntersecting;
        }
        if (!this.isInViewport) {
          // Freeze the effect while offscreen
          this.stopAnimationLoop();
          if (this.autoModeTimeout) {
            clearTimeout(this.autoModeTimeout);
            this.autoModeTimeout = null;
          }
          this.clearConnectionsImmediate();
          this.activeElement = null;
          if (this.mode === 'auto') this.mode = 'idle';
        } else if (this.ready && this.mode === 'idle') {
          this.startAutoMode();
        } else if (this.ready && this.activeElement && !this.animationFrameId) {
          this.startAnimationLoop();
        }
      },
      { threshold: 0, rootMargin: '100px' }
    );
    this.intersectionObserver.observe(this.container);

    this.resizeObserver = new ResizeObserver(() => {
      // Debounce — marquee.js rebuilds clones on a 250ms debounce, so we wait longer
      if (this.resizeDebounceId) clearTimeout(this.resizeDebounceId);
      this.resizeDebounceId = setTimeout(() => {
        this.resizeDebounceId = null;
        this.updateLayoutCache();
      }, 300);
    });
    this.resizeObserver.observe(this.container);
    this.marqueeComponents.forEach((m) => this.resizeObserver.observe(m));

    this.scrollHandler = () => {
      this.scrollDirty = true;
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true, capture: true });
  }

  updateLayoutCache() {
    if (!this.container || !this.container.isConnected) return;

    this.containerRect = this.container.getBoundingClientRect();

    this.marqueeCache = this.marqueeComponents.map((marquee) => {
      const wrapper = /** @type {HTMLElement | null} */ (marquee.querySelector('.marquee__wrapper'));
      const marqueeRect = marquee.getBoundingClientRect();
      const wrapperRect = wrapper ? wrapper.getBoundingClientRect() : marqueeRect;
      const logoEls = /** @type {HTMLElement[]} */ (
        Array.from(marquee.querySelectorAll('.marquee__item-image'))
      );
      const logos = logoEls.map((el) => {
        const r = el.getBoundingClientRect();
        return {
          el,
          dx: r.left - wrapperRect.left,
          dy: r.top - wrapperRect.top,
          w: r.width,
          h: r.height,
        };
      });
      return { marquee, wrapper, marqueeRect, wrapperRect, logos };
    });

    if (this.svgOverlay && this.containerRect) {
      this.svgOverlay.setAttribute(
        'viewBox',
        `0 0 ${this.containerRect.width} ${this.containerRect.height}`
      );
    }
  }

  attachPointerListeners() {
    if (!this.container) return;
    this.container.addEventListener('pointerenter', this.handlePointerEnter.bind(this), true);
    this.container.addEventListener('pointerleave', this.handlePointerLeave.bind(this), true);
    this.container.addEventListener('pointerleave', () => this.clearUserMode());
  }

  resolveConnectionColor() {
    if (!this.container) return;
    const computed = getComputedStyle(this.container);
    const buttonColor = computed.getPropertyValue('--color-primary-button-background').trim();
    this.connectionColor = buttonColor || '#ffffff';
    this.container.style.setProperty('--logo-connection-color', this.connectionColor);
  }

  clearUserMode() {
    if (this.mode !== 'user') return;

    this.mode = 'idle';
    this.activeElement = null;
    this.stopAnimationLoop();
    this.clearConnectionsImmediate();

    if (this.autoModeTimeout) clearTimeout(this.autoModeTimeout);
    this.autoModeTimeout = setTimeout(() => {
      if (this.isInViewport) this.startAutoMode();
    }, 500);
  }

  /** @param {PointerEvent} event */
  handlePointerEnter(event) {
    if (!this.ready) return;
    const target = /** @type {HTMLElement | null} */ (event.target);
    const logoItem = /** @type {HTMLElement | null} */ (target?.closest('.marquee__item-image'));
    if (!logoItem) return;

    if (this.activeElement === logoItem && this.mode === 'user') return;

    this.mode = 'user';

    if (this.autoModeTimeout) {
      clearTimeout(this.autoModeTimeout);
      this.autoModeTimeout = null;
    }
    if (this.autoConnectionDurationTimeout) {
      clearTimeout(this.autoConnectionDurationTimeout);
      this.autoConnectionDurationTimeout = null;
    }

    if (this.activeElement && this.activeElement !== logoItem) {
      this.clearConnectionsImmediate();
    }

    this.activeElement = logoItem;
    this.startAnimationLoop();
  }

  handlePointerLeave(event) {
    const logoItem = event.target.closest('.marquee__item-image');
    if (!logoItem || logoItem !== this.activeElement || this.mode !== 'user') return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget && relatedTarget.closest?.('.marquee__item-image')) {
      return;
    }
    this.clearUserMode();
  }

  startAnimationLoop() {
    if (this.animationFrameId) return;
    if (!this.ready || !this.isInViewport) return;

    // 10 FPS is enough for a passive background effect; hover gets 15 FPS for snappier feedback
    this.targetFPS = this.mode === 'auto' ? 10 : 15;
    this.frameInterval = 1000 / this.targetFPS;

    if (this.mode === 'auto') {
      if (this.autoConnectionDurationTimeout) clearTimeout(this.autoConnectionDurationTimeout);
      this.autoConnectionDurationTimeout = setTimeout(() => {
        this.endAutoConnection();
      }, this.autoHoverDuration || 3000);
    }

    const animate = (timestamp) => {
      const elapsed = timestamp - this.lastFrameTime;
      if (elapsed >= this.frameInterval) {
        this.lastFrameTime = timestamp - (elapsed % this.frameInterval);
        this.updateFrame();
      }
      this.animationFrameId = requestAnimationFrame(animate);
    };
    this.animationFrameId = requestAnimationFrame(animate);
  }

  stopAnimationLoop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.lastFrameTime = 0;
    if (this.autoConnectionDurationTimeout) {
      clearTimeout(this.autoConnectionDurationTimeout);
      this.autoConnectionDurationTimeout = null;
    }
  }

  updateFrame() {
    if (!this.container || !this.container.isConnected) {
      this.stopAnimationLoop();
      return;
    }
    if (!this.activeElement || this.marqueeCache.length === 0) return;

    // ---- READ PHASE ----
    // On scroll, refresh cached container/marquee rects (they aren't transformed).
    if (this.scrollDirty) {
      this.scrollDirty = false;
      this.containerRect = this.container.getBoundingClientRect();
      for (const entry of this.marqueeCache) {
        entry.marqueeRect = entry.marquee.getBoundingClientRect();
      }
    }

    // Read only the wrapper rects (one per marquee) — all logo positions derive from these.
    for (const entry of this.marqueeCache) {
      entry.wrapperRect = entry.wrapper
        ? entry.wrapper.getBoundingClientRect()
        : entry.marqueeRect;
    }

    const activeLookup = this.findCachedLogo(this.activeElement);
    if (!activeLookup) {
      this.onProximityLost();
      return;
    }

    const activeEntry = this.marqueeCache[activeLookup.rowIndex];
    const activeLogo = activeEntry?.logos[activeLookup.logoIndex];
    if (!activeEntry || !activeLogo) {
      this.onProximityLost();
      return;
    }

    if (!this.isLogoVisible(activeLogo, activeEntry)) {
      this.onProximityLost();
      return;
    }

    const activeCenterX = activeEntry.wrapperRect.left + activeLogo.dx + activeLogo.w / 2;
    const activeCenterY = activeEntry.wrapperRect.top + activeLogo.dy + activeLogo.h / 2;

    const adjacentIndices = [];
    if (activeLookup.rowIndex > 0) adjacentIndices.push(activeLookup.rowIndex - 1);
    if (activeLookup.rowIndex < this.marqueeCache.length - 1) {
      adjacentIndices.push(activeLookup.rowIndex + 1);
    }

    /** @type {Connection[]} */
    const newConnections = [];
    for (const rowIdx of adjacentIndices) {
      const entry = this.marqueeCache[rowIdx];
      if (!entry) continue;
      /** @type {Connection | null} */
      let best1 = null;
      /** @type {Connection | null} */
      let best2 = null;

      for (const logo of entry.logos) {
        if (!this.isLogoVisible(logo, entry)) continue;
        const centerX = entry.wrapperRect.left + logo.dx + logo.w / 2;
        const distance = Math.abs(centerX - activeCenterX);
        if (distance >= 300) continue;

        const centerY = entry.wrapperRect.top + logo.dy + logo.h / 2;
        /** @type {Connection} */
        const candidate = { el: logo.el, x: centerX, y: centerY, distance };

        if (!best1 || distance < best1.distance) {
          best2 = best1;
          best1 = candidate;
        } else if (!best2 || distance < best2.distance) {
          best2 = candidate;
        }
      }

      if (best1) newConnections.push(best1);
      if (best2) newConnections.push(best2);
    }

    if (newConnections.length === 0) {
      this.onProximityLost();
      return;
    }

    // ---- WRITE PHASE ----
    this.renderConnections(activeCenterX, activeCenterY, newConnections);
  }

  /**
   * @param {HTMLElement | null} element
   * @returns {{ rowIndex: number, logoIndex: number } | null}
   */
  findCachedLogo(element) {
    if (!element) return null;
    for (let i = 0; i < this.marqueeCache.length; i++) {
      const entry = this.marqueeCache[i];
      if (!entry) continue;
      const logos = entry.logos;
      for (let j = 0; j < logos.length; j++) {
        if (logos[j]?.el === element) return { rowIndex: i, logoIndex: j };
      }
    }
    return null;
  }

  /**
   * @param {CachedLogo} logo
   * @param {MarqueeCacheEntry} entry
   */
  isLogoVisible(logo, entry) {
    const logoLeft = entry.wrapperRect.left + logo.dx;
    const logoRight = logoLeft + logo.w;
    return logoLeft < entry.marqueeRect.right && logoRight > entry.marqueeRect.left;
  }

  /**
   * @param {number} activeCenterX
   * @param {number} activeCenterY
   * @param {Connection[]} newConnections
   */
  renderConnections(activeCenterX, activeCenterY, newConnections) {
    if (!this.svgOverlay || !this.svgOverlay.isConnected) {
      this.createSVGOverlay();
      if (!this.svgOverlay) return;
    }
    if (!this.containerRect) return;

    const offsetX = this.containerRect.left;
    const offsetY = this.containerRect.top;

    // Build the new highlighted set (active + connected)
    const newSet = new Set();
    newSet.add(this.activeElement);
    for (const c of newConnections) newSet.add(c.el);

    // Remove class from elements no longer in set
    for (const el of this.prevConnectedSet) {
      if (!newSet.has(el)) el.classList.remove('logo-connected', 'logo-active');
    }
    // Add class to newly connected
    for (const el of newSet) {
      if (!this.prevConnectedSet.has(el)) el.classList.add('logo-connected');
    }

    // Swap .logo-active if needed
    if (this.prevActiveEl && this.prevActiveEl !== this.activeElement) {
      this.prevActiveEl.classList.remove('logo-active');
    }
    if (this.activeElement && this.prevActiveEl !== this.activeElement) {
      this.activeElement.classList.add('logo-active');
    }
    this.prevActiveEl = this.activeElement;
    this.prevConnectedSet = newSet;

    // Update lines from the pool (reuse SVG elements)
    const needed = newConnections.length;
    for (let i = 0; i < needed; i++) {
      const c = newConnections[i];
      if (!c) continue;
      let line = this.linePool[i];
      if (!line) {
        line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.classList.add('logo-connection-line');
        line.setAttribute('stroke', this.connectionColor);
        this.svgOverlay.appendChild(line);
        this.linePool[i] = line;
      }
      line.setAttribute('x1', String(activeCenterX - offsetX));
      line.setAttribute('y1', String(activeCenterY - offsetY));
      line.setAttribute('x2', String(c.x - offsetX));
      line.setAttribute('y2', String(c.y - offsetY));
      if (line.style.display === 'none') line.style.display = '';
    }
    for (let i = needed; i < this.linePool.length; i++) {
      const line = this.linePool[i];
      if (line && line.style.display !== 'none') line.style.display = 'none';
    }

    this.connections = newConnections;
  }

  onProximityLost() {
    if (this.autoConnectionDurationTimeout) {
      clearTimeout(this.autoConnectionDurationTimeout);
      this.autoConnectionDurationTimeout = null;
    }

    if (this.mode === 'user') {
      this.clearUserMode();
    } else if (this.mode === 'auto') {
      this.stopAnimationLoop();
      this.clearConnectionsImmediate();
      this.activeElement = null;

      setTimeout(() => {
        if (this.mode === 'auto' && this.isInViewport) this.pickRandomConnection();
      }, 300);
    }
  }

  endAutoConnection() {
    if (this.mode !== 'auto') return;

    this.stopAnimationLoop();
    this.clearConnectionsImmediate();
    this.activeElement = null;

    setTimeout(() => {
      if (this.mode === 'auto' && this.isInViewport) this.pickRandomConnection();
    }, 300);
  }

  startAutoMode() {
    if (this.mode !== 'idle' || !this.ready || !this.isInViewport) return;

    this.mode = 'auto';
    this.pickRandomConnection();
  }

  pickRandomConnection() {
    if (this.mode !== 'auto' || !this.isInViewport) return;

    const randomLogo = this.pickRandomVisibleLogo();
    if (!randomLogo) {
      setTimeout(() => this.pickRandomConnection(), 1000);
      return;
    }

    this.activeElement = randomLogo;
    this.startAnimationLoop();
  }

  pickRandomVisibleLogo() {
    const middleRowIndex = Math.floor(this.marqueeCache.length / 2);
    const entry = this.marqueeCache[middleRowIndex];
    if (!entry) return null;

    // Refresh wrapper rect once to test visibility
    if (entry.wrapper) {
      entry.wrapperRect = entry.wrapper.getBoundingClientRect();
    }

    const visible = [];
    for (const logo of entry.logos) {
      if (this.isLogoVisible(logo, entry)) visible.push(logo);
    }
    if (visible.length === 0) return null;

    return visible[Math.floor(Math.random() * visible.length)].el;
  }

  createSVGOverlay() {
    if (!this.container || !this.container.isConnected) {
      console.warn('[LogoConnections] Cannot create SVG overlay: container not found or disconnected');
      return;
    }

    if (this.svgOverlay) {
      this.svgOverlay.remove();
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add('logo-connections-overlay', 'active');
    svg.setAttribute('aria-hidden', 'true');

    const rect = this.containerRect || this.container.getBoundingClientRect();
    this.containerRect = rect;

    svg.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;';
    svg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);

    this.container.appendChild(svg);
    this.svgOverlay = svg;
    this.linePool = [];
  }

  clearConnectionsImmediate() {
    for (const line of this.linePool) {
      if (line.style.display !== 'none') line.style.display = 'none';
    }
    for (const el of this.prevConnectedSet) {
      el.classList.remove('logo-connected', 'logo-active');
    }
    if (this.prevActiveEl) this.prevActiveEl.classList.remove('logo-active');
    this.prevConnectedSet = new Set();
    this.prevActiveEl = null;
    this.connections = [];
  }
}

// Inject styles — single drop-shadow instead of 3-4 stacked layers (much cheaper compositing),
// plus CSS containment on marquees to scope layout/paint invalidation.
const style = document.createElement('style');
style.textContent = `
  .group-block-content:has(marquee-component[data-connections-enabled="true"]),
  .section-content-wrapper:has(marquee-component[data-connections-enabled="true"]) {
    position: relative;
  }

  marquee-component[data-connections-enabled="true"] {
    overflow: visible !important;
    position: relative;
    z-index: 1;
    contain: layout paint;
  }

  marquee-component[data-connections-enabled="true"] .marquee__wrapper,
  marquee-component[data-connections-enabled="true"] .marquee__content {
    overflow: visible !important;
  }

  .logo-connected {
    filter: drop-shadow(0 0 2px var(--logo-connection-color, #ffffff));
    z-index: 10;
    position: relative;
  }

  .logo-connected.logo-active {
    filter: drop-shadow(0 0 3px var(--logo-connection-color, #ffffff));
  }

  .logo-connections-overlay {
    z-index: 0;
  }

  .logo-connections-overlay.active {
    opacity: 1;
  }

  .logo-connection-line {
    stroke-width: 2;
    opacity: 0.8;
    stroke-linecap: round;
  }
`;
document.head.appendChild(style);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeLogoConnections);
} else {
  initializeLogoConnections();
}

function initializeLogoConnections() {
  const enabledMarquees = Array.from(
    document.querySelectorAll('marquee-component[data-connections-enabled="true"]')
  );

  if (enabledMarquees.length < 2) return;

  const containers = new Map();
  enabledMarquees.forEach((marquee) => {
    const parent = marquee.closest('.group-block-content, .section-content-wrapper');
    if (!parent) return;
    if (!containers.has(parent)) containers.set(parent, []);
    containers.get(parent).push(marquee);
  });

  containers.forEach((marquees, container) => {
    if (marquees.length >= 2) {
      const manager = new LogoConnectionsManager();
      manager.marqueeComponents = marquees;
      manager.container = container;
      manager.setupEventListeners();
    }
  });
}
